/**
 * SSML Readme のカテゴリ切替タブ。
 *
 * 既存の接続線 / 方面タブ (`ConnectingDirectionsSection`) と同じ
 * `role="tablist"` + `role="tab"` + `aria-selected` の実装パターンに倣う
 * (`docs/ssml-tuning-plan.md` 8.4.3)。
 *
 * ## 設計上の約束
 *
 * - **データは props で受ける**。本コンポーネントは `categories.ts` を import しない。
 *   Phase2 で Readme が API 化されても、渡す配列の出所が変わるだけで作り直しが発生しない
 *   (計画のリスク R14)。
 * - 選択状態は親 (`SsmlReadmePage`) が保持する。タブは「押されたことを伝える」だけに留め、
 *   どのカテゴリを開いているかという状態を二重に持たない。
 */
import type React from 'react';

import type { SsmlReadmeCategory, SsmlReadmeCategoryKey } from './types';

export interface ReadmeCategoryNavProps {
  /** タブに並べるカテゴリ (配列順がタブの並び順になる) */
  categories: readonly SsmlReadmeCategory[];
  /** 選択中のカテゴリキー */
  activeCategory: SsmlReadmeCategoryKey;
  /** タブが押されたときの通知 (選択状態の保持は親の責務) */
  onSelectCategory: (category: SsmlReadmeCategoryKey) => void;
}

export function ReadmeCategoryNav({
  categories,
  activeCategory,
  onSelectCategory,
}: ReadmeCategoryNavProps): React.JSX.Element {
  return (
    <div
      className="ssml-readme-tabs"
      role="tablist"
      aria-label="SSML カテゴリ"
      data-testid="ssml-readme-tabs"
    >
      {categories.map((category) => {
        const isActive = category.category === activeCategory;
        return (
          <button
            key={category.category}
            type="button"
            role="tab"
            aria-selected={isActive}
            /*
             * タブと本文を関連付ける。本文側 (`ReadmeEntryList`) は
             * `role="tabpanel"` + 同 ID を持つため、支援技術で対応関係が辿れる。
             */
            aria-controls={`ssml-readme-panel-${category.category}`}
            id={`ssml-readme-tab-${category.category}`}
            className={`ssml-readme-tab${isActive ? ' is-active' : ''}`}
            data-testid={`ssml-readme-tab-${category.category}`}
            onClick={() => onSelectCategory(category.category)}
          >
            {category.label}
            {/*
              件数を併記する: 「このカテゴリに何件あるか」がタブを開かずに分かると、
              参照先を探す往復が減る (Readme は読むだけの画面なので探索性が価値になる)。
            */}
            <span className="ssml-readme-tab__count">{category.entries.length}</span>
          </button>
        );
      })}
    </div>
  );
}
